/**
 * React context for the SPA's authentication state.
 *
 * The provider exposes the in-memory token set, the decoded ID-token
 * claims, and helpers to begin/complete sign-in and sign out. The id and
 * access tokens are held in memory only; the **refresh token** is persisted to
 * `localStorage` so a page refresh or a new tab can renew the session silently
 * via `grant_type=refresh_token` (a plain token-endpoint call — never an
 * authorize redirect, so it cannot loop). The operator is only prompted to sign
 * in when there is no stored refresh token or it has expired / been revoked.
 *
 * Security tradeoff (accepted for this internal operator tool): a refresh token
 * in `localStorage` is reachable by XSS. It is the only persisted credential;
 * `signOut` clears it and redirects through the Hosted UI logout.
 *
 * Validates: Requirements 8.1, 8.3, 8.4
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  beginLogin,
  buildLogoutUrl,
  clearStoredRefreshToken,
  completeLogin,
  decodeIdToken,
  loadStoredRefreshToken,
  persistRefreshToken,
  refreshTokens,
  type CognitoConfig,
  type CognitoTokens,
  type IdTokenClaims,
} from './cognito';

/** Auth state exposed to consumers. */
export interface AuthState {
  /** `true` while the SPA is exchanging an auth-code for tokens on first load. */
  loading: boolean;
  /** Most recent error from sign-in. Cleared when a sign-in succeeds. */
  error: string | null;
  tokens: CognitoTokens | null;
  claims: IdTokenClaims | null;
  signIn: (identityProvider?: string) => Promise<void>;
  signOut: () => void;
  /** Pulls the current access token, useful for the api-client fetch wrapper. */
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

interface AuthProviderProps {
  config: CognitoConfig;
  children: ReactNode;
  /** Test seam: skip running `completeLogin()` on first render. */
  skipBootstrap?: boolean;
}

/**
 * Wraps the app and surfaces auth state via {@link useAuth}.
 *
 * On first mount, attempts to complete a Hosted UI redirect by reading
 * `?code` from the URL. If successful, the URL is rewritten to drop the
 * `?code` so a refresh does not retry the exchange.
 */
export function AuthProvider({
  config,
  children,
  skipBootstrap = false,
}: AuthProviderProps): JSX.Element {
  const [tokens, setTokens] = useState<CognitoTokens | null>(null);
  const [claims, setClaims] = useState<IdTokenClaims | null>(null);
  const [loading, setLoading] = useState(!skipBootstrap);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (skipBootstrap) return;
    let cancelled = false;
    void (async () => {
      try {
        // (1) Returning from the Hosted UI with an auth code → exchange it and
        // persist the refresh token for future silent renewals.
        const result = await completeLogin(config);
        if (cancelled) return;
        if (result !== null) {
          setTokens(result);
          setClaims(decodeIdToken(result.idToken));
          persistRefreshToken(result.refreshToken);
          // `/callback?code=...` has no route — land on a real page.
          if (window.location.pathname.endsWith('/callback')) {
            navigate('/', { replace: true });
          }
          setLoading(false);
          return;
        }

        // (2) No auth code in the URL. Try to restore a session from a stored
        // refresh token via `grant_type=refresh_token`. This is a plain
        // token-endpoint call — NO authorize redirect — so it can never loop.
        const storedRefresh = loadStoredRefreshToken();
        if (storedRefresh !== null) {
          const refreshed = await refreshTokens(config, storedRefresh);
          if (cancelled) return;
          if (refreshed !== null) {
            setTokens(refreshed);
            setClaims(decodeIdToken(refreshed.idToken));
            persistRefreshToken(refreshed.refreshToken);
          } else {
            // Refresh token expired / revoked / rejected → drop it; the operator
            // must sign in interactively.
            clearStoredRefreshToken();
          }
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [config, skipBootstrap, navigate]);

  const signIn = useCallback(async (identityProvider?: string) => {
    setError(null);
    await beginLogin(config, { identityProvider });
  }, [config]);

  const signOut = useCallback(() => {
    setTokens(null);
    setClaims(null);
    // Drop the persisted refresh token so a post-logout load does NOT silently
    // renew the session; the Hosted UI logout then clears the Cognito cookie.
    clearStoredRefreshToken();
    window.location.assign(buildLogoutUrl(config));
  }, [config]);

  const getAccessToken = useCallback((): string | null => {
    if (tokens === null) return null;
    if (tokens.expiresAt <= Date.now()) return null;
    // API Gateway's Cognito authorizer is configured to validate the
    // ID token, not the access token (we confirmed this empirically:
    // POST /devices with the AccessToken returns Unauthorized; the
    // same call with the IdToken passes the authorizer). The function
    // name is preserved for backwards compatibility with every
    // consumer; the semantics are "the token to send as the Bearer to
    // the TCVS API".
    return tokens.idToken;
  }, [tokens]);

  const value = useMemo<AuthState>(
    () => ({ loading, error, tokens, claims, signIn, signOut, getAccessToken }),
    [loading, error, tokens, claims, signIn, signOut, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook accessor for the {@link AuthContext}. */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
